import { useState } from "react";
import domain from "../domain";
 
import classes from "./OffersComponent.module.css";

const STORAGE_KEY = "offers";

function readStoredProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function offerDraft(profile) {
  return {
    l1Offer: profile?.l1Offer ?? "",
    l1OfferValidity: profile?.l1OfferValidity ?? "",
    l2Offer: profile?.l2Offer ?? "",
    l2OfferValidity: profile?.l2OfferValidity ?? "",
    dailyMessagingLimit: profile?.dailyMessagingLimit ?? "",
  };
}

function parseValidity(value) {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

export default function OffersComponent() {
  const [profile, setProfile] = useState(readStoredProfile);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => offerDraft(readStoredProfile()));

  function persist(next) {
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setProfile(next);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setProfile(null);
    }
  }

  async function loadOffers() {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${domain}/api/getOffers`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const data = await response.json();
      const next = data ?? null;
      persist(next);
      setDraft(offerDraft(next));
      setEditing(false);
    } catch (err) {
      setError(err.message || "Could not load offers");
    } finally {
      setFetched(true);
      setLoading(false);
    }
  }

  function startEdit() {
    setDraft(offerDraft(profile));
    setError("");
    setEditing(true);
  }

  function cancelEdit() {
    setDraft(offerDraft(profile));
    setError("");
    setEditing(false);
  }

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function saveOffers(event) {
    event.preventDefault();
    const l1OfferValidity = parseValidity(draft.l1OfferValidity);
    const l2OfferValidity = parseValidity(draft.l2OfferValidity);
    const dailyMessagingLimit = parseValidity(draft.dailyMessagingLimit);
    const l1Offer = draft.l1Offer.trim();
    const l2Offer = draft.l2Offer.trim();

    if (
      !l1Offer ||
      !l2Offer ||
      l1OfferValidity == null ||
      l2OfferValidity == null ||
      dailyMessagingLimit == null
    ) {
      setError(
        "Enter both offers, a whole number of days greater than 0, and a daily messaging limit greater than 0."
      );
      return;
    }

    

    setSaving(true);
    setError("");

    const body = {
      
      l1Offer,
      l1OfferValidity,
      l2Offer,
      l2OfferValidity,
      dailyMessagingLimit,
    };

    try {
      const response = await fetch(`${domain}/api/setOffers`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      console.log("response after save")
      console.log(response);
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      // Keep brandName and adminType from the loaded profile.
      const next = {
        ...profile,
        l1Offer,
        l1OfferValidity,
        l2Offer,
        l2OfferValidity,
        dailyMessagingLimit,
      };
      persist(next);
      setDraft(offerDraft(next));
      setEditing(false);
    } catch (err) {
      setError(err.message || "Could not save offers");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={classes.offers}>
      <div className={classes.titleRow}>
        <h3>Offers</h3>
        <div className={classes.actions}>
          <button type="button" onClick={loadOffers} disabled={loading || saving}>
            {profile ? "Refresh" : "View"}
          </button>
          {profile && !editing && (
            <button type="button" onClick={startEdit} disabled={loading || saving}>
              Edit
            </button>
          )}
        </div>
      </div>
      {loading && <p>Loading offers...</p>}
      {error && <p className={classes.error}>{error}</p>}
      {!loading && !error && !profile && fetched && <p>No offers found.</p>}
      {profile && !editing && (
        <>
          <div className={classes.header}>
            <span className={classes.brand}>{profile.brandName}</span>
            <span className={classes.badge}>{profile.adminType}</span>
          </div>
          <ul className={classes.list}>
            <li>
              <span className={classes.level}>Level 1</span>
              <p className={classes.text}>{profile.l1Offer}</p>
              <p className={classes.validity}>
                Valid for {profile.l1OfferValidity} days
              </p>
            </li>
            <li>
              <span className={classes.level}>Level 2</span>
              <p className={classes.text}>{profile.l2Offer}</p>
              <p className={classes.validity}>
                Valid for {profile.l2OfferValidity} days
              </p>
            </li>
            <li>
              <span className={classes.level}>Daily messaging limit</span>
              <p className={classes.text}>{profile.dailyMessagingLimit}</p>
            </li>
          </ul>
        </>
      )}
      {profile && editing && (
        <form className={classes.form} onSubmit={saveOffers}>
          <div className={classes.header}>
            <span className={classes.brand}>{profile.brandName}</span>
            <span className={classes.badge}>{profile.adminType}</span>
          </div>
          <label>
            Level 1 offer
            <textarea
              value={draft.l1Offer}
              onChange={(e) => updateDraft("l1Offer", e.target.value)}
              rows={3}
              required
            />
          </label>
          <label>
            Level 1 validity (days)
            <input
              type="number"
              min="1"
              step="1"
              value={draft.l1OfferValidity}
              onChange={(e) => updateDraft("l1OfferValidity", e.target.value)}
              required
            />
          </label>
          <label>
            Level 2 offer
            <textarea
              value={draft.l2Offer}
              onChange={(e) => updateDraft("l2Offer", e.target.value)}
              rows={3}
              required
            />
          </label>
          <label>
            Level 2 validity (days)
            <input
              type="number"
              min="1"
              step="1"
              value={draft.l2OfferValidity}
              onChange={(e) => updateDraft("l2OfferValidity", e.target.value)}
              required
            />
          </label>
          <label>
            Daily messaging limit
            <input
              type="number"
              min="1"
              step="1"
              value={draft.dailyMessagingLimit}
              onChange={(e) => updateDraft("dailyMessagingLimit", e.target.value)}
              required
            />
          </label>
          <div className={classes.actions}>
            <button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button type="button" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
